import { useEffect, useState } from "react";
import api, { apiError } from "../../lib/api";
import { Plus, Trash2, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import ImageUploader, { absUrl } from "../../components/admin/ImageUploader";

const empty = { slug: "", name: "", caption: "", image: "", sort_order: 99, active: true };

export default function AdminCategories() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/categories");
      setCats(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onSave = async (form) => {
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/admin/categories", form);
        toast.success("Category created");
      } else {
        await api.put(`/admin/categories/${editing.id}`, form);
        toast.success("Category updated");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (c) => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    try {
      await api.delete(`/admin/categories/${c.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="p-6 md:p-10" data-testid="admin-categories">
      <header className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <span className="font-script text-3xl text-blush-400">the edit</span>
          <h1 className="font-heading text-3xl md:text-4xl text-ink-900">Categories</h1>
        </div>
        <button
          className="btn-maroon inline-flex items-center gap-2"
          onClick={() => setEditing("new")}
          data-testid="admin-add-category-btn"
        >
          <Plus size={14} strokeWidth={1.6} /> New category
        </button>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full p-10 text-center text-ink-500">Loading…</div>
        ) : cats.length === 0 ? (
          <div className="col-span-full p-10 text-center text-ink-500 border border-dashed border-blush-200 rounded-3xl">
            No categories yet. Create your first one.
          </div>
        ) : cats.map((c) => (
          <article key={c.id} className="rounded-3xl bg-white border border-blush-100 overflow-hidden group">
            <div className="relative aspect-[5/3] bg-blush-50">
              {c.image && <img src={absUrl(c.image)} alt="" className="h-full w-full object-cover" />}
              {!c.active && (
                <span className="absolute top-3 left-3 px-2 py-1 rounded-full bg-ink-900/80 text-white text-[10px] uppercase tracking-[0.18em]">
                  inactive
                </span>
              )}
            </div>
            <div className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-heading text-xl text-ink-900">{c.name}</h3>
                  <p className="text-xs text-ink-500 mt-1">/{c.slug} · sort {c.sort_order}</p>
                  {c.caption && <p className="text-sm text-ink-700 mt-2 font-body">{c.caption}</p>}
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setEditing(c)} className="h-8 w-8 grid place-items-center rounded-full hover:bg-blush-50 text-ink-700" data-testid={`cat-edit-${c.id}`}>
                    <Pencil size={14} strokeWidth={1.6} />
                  </button>
                  <button onClick={() => onDelete(c)} className="h-8 w-8 grid place-items-center rounded-full hover:bg-blush-50 text-blush-600" data-testid={`cat-delete-${c.id}`}>
                    <Trash2 size={14} strokeWidth={1.6} />
                  </button>
                </div>
              </div>
            </div>
          </article>
        ))}
      </div>

      {editing && (
        <CategoryDrawer
          initial={editing === "new" ? empty : editing}
          onSave={onSave}
          onClose={() => setEditing(null)}
          saving={saving}
          isNew={editing === "new"}
        />
      )}
    </div>
  );
}

function CategoryDrawer({ initial, onSave, onClose, saving, isNew }) {
  const [f, setF] = useState({ ...empty, ...initial });
  const update = (k, v) => setF((s) => ({ ...s, [k]: v }));

  return (
    <div className="fixed inset-0 z-50 bg-ink-900/40 backdrop-blur-sm flex justify-end" onClick={onClose}>
      <div className="w-full md:max-w-md bg-cream-100 h-full overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-category-drawer">
        <div className="sticky top-0 z-10 bg-cream-100/95 backdrop-blur border-b border-blush-100 px-6 py-5 flex items-center justify-between">
          <h2 className="font-heading text-xl text-ink-900">{isNew ? "New category" : "Edit category"}</h2>
          <button onClick={onClose} className="h-9 w-9 rounded-full grid place-items-center hover:bg-blush-50">
            <X size={18} strokeWidth={1.6} />
          </button>
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); onSave({ ...f, sort_order: Number(f.sort_order) }); }}
          className="p-6 space-y-4"
        >
          <Field label="Name" required value={f.name} onChange={(v) => update("name", v)} placeholder="Casual Kurtis" />
          <Field label="Slug" required value={f.slug} onChange={(v) => update("slug", v)} placeholder="casual" disabled={!isNew} hint={!isNew ? "Slug is immutable after creation." : null} />
          <Field label="Caption" value={f.caption} onChange={(v) => update("caption", v)} placeholder="Effortless everyday elegance" />
          <div>
            <label className="text-xs font-body uppercase tracking-[0.2em] text-ink-500">Cover image</label>
            <div className="mt-2 max-w-xs">
              <ImageUploader value={f.image} onChange={(v) => update("image", v)} onRemove={() => update("image", "")} />
            </div>
          </div>
          <Field label="Sort order" type="number" value={f.sort_order} onChange={(v) => update("sort_order", v)} />
          <div className="flex items-center gap-2">
            <input type="checkbox" id="cat-active" checked={f.active} onChange={(e) => update("active", e.target.checked)} />
            <label htmlFor="cat-active" className="font-body text-sm">Active (visible on storefront)</label>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-blush-100">
            <button type="button" onClick={onClose} className="btn-ghost-rose">Cancel</button>
            <button type="submit" disabled={saving} className="btn-maroon disabled:opacity-60" data-testid="admin-category-save">
              {saving ? "Saving…" : (isNew ? "Create" : "Save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, required, placeholder, type = "text", disabled, hint }) {
  return (
    <div>
      <label className="text-xs font-body uppercase tracking-[0.2em] text-ink-500">{label}</label>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} required={required} placeholder={placeholder} disabled={disabled} className="field mt-2 disabled:opacity-60" />
      {hint && <p className="mt-1 text-[11px] font-body text-ink-500">{hint}</p>}
    </div>
  );
}
